import React from 'react';
import { RefreshCw } from 'lucide-react';

interface PageHeaderProps {
  /** Lucide icon component to display in the icon ring */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: React.ComponentType<any>;
  /** Main page title */
  title: string;
  /** Optional subtitle or description */
  subtitle?: string;
  /** Optional badge text (e.g., "Beta") displayed next to the title */
  badge?: string;
  /** Callback when refresh button is clicked */
  onRefresh?: () => void;
  /** Whether data is currently being refreshed */
  isRefreshing?: boolean;
  /** Optional additional elements to render on the right side */
  actions?: React.ReactNode;
  /** Optional className for the root element */
  className?: string;
}

/**
 * Unified page header with Soft Glow design.
 *
 * Renders a brand-blue radial glow behind the icon, a circular icon ring,
 * title with optional badge, subtitle, actions slot, and a fading gradient rule.
 *
 * @example
 * ```tsx
 * <PageHeader
 *   icon={Clock}
 *   title="Settlement Age Report"
 *   subtitle="Track customer retention and outreach based on loan age"
 *   onRefresh={() => refetch()}
 *   isRefreshing={loading}
 * />
 * ```
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  icon: Icon,
  title,
  subtitle,
  badge,
  onRefresh,
  isRefreshing = false,
  actions,
  className = '',
}) => {
  return (
    <div className={`pt-5 pb-4 relative overflow-hidden ${className}`}>
      {/* Radial glow — always brand blue */}
      <div
        className="absolute top-[-40px] left-[10px] w-[220px] h-[140px] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse, rgba(37, 99, 235, 0.15) 0%, transparent 70%)',
        }}
      />

      {/* Content row */}
      <div className="relative z-10 flex items-start justify-between gap-4">
        {/* Left: icon + title block */}
        <div className="flex items-start gap-3.5">
          {Icon && (
            <div
              className="mt-0.5 w-[2.625rem] h-[2.625rem] rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                background:
                  'linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(37, 99, 235, 0.06))',
                boxShadow: '0 0 24px rgba(37, 99, 235, 0.12)',
              }}
            >
              <Icon className="w-5 h-5 text-blue-400" strokeWidth={1.75} />
            </div>
          )}

          <div className="min-w-0">
            {/* Title row with optional badge */}
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-2xl font-bold text-white tracking-tight leading-tight">
                {title}
              </h2>
              {badge && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[0.625rem] font-bold uppercase tracking-wider bg-blue-500/10 text-blue-400">
                  {badge}
                </span>
              )}
            </div>

            {subtitle && (
              <p className="text-gray-400 text-sm mt-1 leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Right: actions + optional refresh */}
        {(actions || onRefresh) && (
          <div className="flex items-center gap-2 flex-shrink-0 mt-1">
            {actions}
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium border border-gray-600/50 text-gray-400 hover:text-white hover:bg-white/[0.04] hover:border-gray-500/30 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw
                  className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
                />
                <span>Refresh</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Fading gradient rule */}
      <div
        className="relative z-10 mt-5 h-px"
        style={{
          background:
            'linear-gradient(90deg, rgba(75, 85, 99, 0.3), transparent 60%)',
        }}
      />
    </div>
  );
};

export default PageHeader;
